/**
 * ProjectsGrid - Column definitions and grid configuration for the Projects table.
 *
 * Architecture (following EBTimeEntry's TimesheetReportGrid pattern):
 *   ProjectsPage (data fetching, filters, pagination state)
 *     └─ ProjectsGrid (column definitions, cell renderers)  ← this file
 *         └─ DataTableEnhanced (generic table rendering)
 *
 * All cell rendering logic that was previously in the monolithic renderColumnValue
 * switch statement in projects.tsx is now expressed as TanStack ColumnDef cell renderers.
 */

import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle } from 'lucide-react';
import { SensitiveData } from '@/components/sensitive-data';
import {
  DataTableEnhanced,
  columnDefinitionsToVisibility,
  type ColumnSort,
} from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import {
  formatCurrency,
  formatPercent,
  formatDate,
  getHealthBadgeColor,
  formatStatus,
  getVarianceColor,
  calculateDaysRemaining,
  calculateForecastAtCompletion,
  formatProjectType,
  formatRelativeTime,
  getDaysRemainingColor,
  getProjectTypeColor,
  getWIPColor,
} from '@/lib/utils';
import { getProjectStatusColor } from '@/lib/status-colors';
import type { Project, Client, Employee, Task, Milestone, ResourceAllocation } from '@/types';
import { PROJECT_STATUS_LABELS } from '@/types';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import type { ColumnDefinition } from '@/components/column-configurator-drawer';
import type { PaginationMetadata } from '@/types/pagination';

// ── Types ────────────────────────────────────────────────────────────────

/** Reference data needed by cell renderers */
export interface ProjectsGridReferenceData {
  clients: Client[];
  employees: Employee[];
  tasks: Task[];
  milestones: Milestone[];
  resourceAllocations: ResourceAllocation[];
}

export interface ProjectsGridProps {
  /** Pre-sorted/paginated project data to render */
  data: Project[];
  /** Reference data for lookups in cell renderers */
  referenceData: ProjectsGridReferenceData;
  /** Column definitions from ColumnConfiguratorDrawer (controls visibility & order) */
  columnDefinitions: ColumnDefinition[];
  /** Multi-column sort state */
  columnSorts: ColumnSort[];
  /** Callback when sort state changes */
  onColumnSortChange: (sorts: ColumnSort[]) => void;
  /** Callback when a project row is clicked */
  onRowClick: (project: Project) => void;
  /** Show loading skeleton */
  loading?: boolean;
  /** Show transition overlay */
  isTransitioning?: boolean;
  /** Number of skeleton rows when loading */
  pageSize?: number;
  /** Current search/filter state for empty message */
  hasActiveFilters?: boolean;
  /** Pagination metadata for pagination controls */
  pagination?: PaginationMetadata;
  /** Callback for page changes */
  onPageChange?: (page: number) => void;
  /** Callback for page size changes */
  onPageSizeChange?: (size: number) => void;
}

// ── Helper functions (extracted from projects.tsx) ───────────────────────

function getClientNames(project: Project, clients: Client[]): string {
  if (project.clientNames) return project.clientNames;
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const names = ids.map(
      (id) => clients.find((c) => c.id === parseInt(id, 10))?.name || 'Unknown',
    );
    return names.join(', ');
  }
  return 'Unknown';
}

function getClientCodes(project: Project, clients: Client[]): string {
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const codes = ids.map(
      (id) => clients.find((c) => c.id === parseInt(id, 10))?.code || '-',
    );
    return codes.join(', ');
  }
  return '-';
}

function getManagerName(managerId: number, employees: Employee[]): string {
  return employees.find((e) => e.id === managerId)?.name || 'Unknown';
}

function getClientContacts(project: Project, clients: Client[]): string {
  if (project.clientIds) {
    const ids = project.clientIds.split(',').filter(Boolean);
    const contacts = ids
      .map((id) => clients.find((c) => c.id === parseInt(id, 10))?.contactName)
      .filter(Boolean);
    return contacts.length > 0 ? contacts.join(', ') : '-';
  }
  return '-';
}

function getOverdueTasks(projectId: number, tasks: Task[]): number {
  const now = new Date();
  return tasks.filter(
    (t) =>
      t.projectId === projectId &&
      t.status !== 'completed' &&
      new Date(t.dueDate) < now,
  ).length;
}

function getOverdueMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter(
    (m) => m.projectId === projectId && m.status === 'overdue',
  ).length;
}

function getNextMilestone(
  projectId: number,
  milestones: Milestone[],
): Milestone | undefined {
  const upcoming = milestones
    .filter((m) => m.projectId === projectId && m.status === 'pending')
    .sort(
      (a, b) =>
        new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
    );
  return upcoming[0];
}

function getAllocatedResources(
  projectId: number,
  allocations: ResourceAllocation[],
): number {
  const uniqueEmployees = new Set(
    allocations
      .filter((a) => a.projectId === projectId)
      .map((a) => a.employeeId),
  );
  return uniqueEmployees.size;
}


function getCompletedMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter(
    (m) => m.projectId === projectId && m.status === 'completed',
  ).length;
}

function getTotalMilestones(projectId: number, milestones: Milestone[]): number {
  return milestones.filter((m) => m.projectId === projectId).length;
}

function getUtilizationRate(project: Project): number {
  if (project.budgetHours === 0) return 0;
  return (project.actualHours / project.budgetHours) * 100;
}

function calculateDuration(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// ── Column builder ───────────────────────────────────────────────────────

/**
 * Build the full set of TanStack column definitions for the Projects grid.
 * Each column ID matches the ColumnDefinition IDs from column-configurator-drawer.
 * Cell renderers use the provided reference data for lookups.
 */
function buildProjectColumns(
  ref: ProjectsGridReferenceData,
  statusLabels: Record<number, string>,
): ColumnDef<Project, unknown>[] {
  return [
    {
      id: 'project-name',
      accessorFn: (row) => row.name,
      header: 'Project Name',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.name}</div>
      ),
    },
    {
      id: 'project-code',
      accessorFn: (row) => row.code,
      header: 'Project Code',
      cell: ({ row }) => row.original.code || '-',
    },
    {
      id: 'client-code',
      accessorFn: (row) => getClientCodes(row, ref.clients),
      header: 'Client Code',
      cell: ({ row }) => getClientCodes(row.original, ref.clients),
    },
    {
      id: 'client-name',
      accessorFn: (row) => getClientNames(row, ref.clients),
      header: 'Client Name',
      cell: ({ row }) => {
        const project = row.original;
        if (!project.clientIds) return <span>-</span>;
        const ids = project.clientIds.split(',').filter(Boolean);
        return (
          <div className="flex flex-col gap-0.5">
            {ids.map((id) => {
              const client = ref.clients.find((c) => c.id === parseInt(id, 10));
              if (!client) return null;
              const isOnStop = client.accountStatus === 'on-stop' || client.accountStatus === 'suspended';
              return (
                <div key={id} className="flex items-center gap-1.5">
                  <span className="font-medium">{client.name}</span>
                  {isOnStop && (
                    <span className="inline-flex items-center gap-0.5 text-xs font-medium text-amber-700 bg-amber-100 border border-amber-300 rounded px-1 py-0 leading-4 shrink-0">
                      <AlertTriangle className="h-2.5 w-2.5" />
                      On Stop
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      id: 'client-contact',
      accessorFn: (row) => getClientContacts(row, ref.clients),
      header: 'Client Contact',
      cell: ({ row }) => getClientContacts(row.original, ref.clients),
    },
    {
      id: 'account-manager',
      accessorFn: (row) => {
        if (!row.clientIds) return '-';
        const ids = row.clientIds.split(',').filter(Boolean);
        const names = [
          ...new Set(
            ids
              .map((id) => ref.clients.find((c) => c.id === parseInt(id, 10))?.accountManagerName)
              .filter(Boolean),
          ),
        ];
        return names.length > 0 ? names.join(', ') : '-';
      },
      header: 'Account Manager',
      cell: ({ row }) => {
        if (!row.original.clientIds) return <span className="text-muted-foreground text-sm">-</span>;
        const ids = row.original.clientIds.split(',').filter(Boolean);
        const names = [
          ...new Set(
            ids
              .map((id) => ref.clients.find((c) => c.id === parseInt(id, 10))?.accountManagerName)
              .filter(Boolean),
          ),
        ];
        if (names.length === 0) return <span className="text-muted-foreground text-sm">-</span>;
        return <span>{names.join(', ')}</span>;
      },
    },
    {
      id: 'project-manager',
      accessorFn: (row) => getManagerName(row.projectManagerId, ref.employees),
      header: 'Project Manager',
      cell: ({ row }) =>
        getManagerName(row.original.projectManagerId, ref.employees),
    },
    {
      id: 'project-status',
      accessorFn: (row) => row.statusId,
      header: 'Project Status',
      cell: ({ row }) => {
        if (row.original.statusId == null) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <Badge
            variant="outline"
            className={getProjectStatusColor(row.original.statusId)}
            disableHover
          >
            {statusLabels[row.original.statusId] || PROJECT_STATUS_LABELS[row.original.statusId] || formatStatus(String(row.original.statusId))}
          </Badge>
        );
      },
    },
    {
      id: 'health-indicator',
      accessorFn: (row) => row.health,
      header: 'Health Indicator',
      cell: ({ row }) => {
        if (!row.original.health) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <Badge
            className={getHealthBadgeColor(row.original.health)}
            disableHover
          >
            {row.original.health === 'green' ? 'Healthy' : row.original.health === 'amber' ? 'At Risk' : 'Critical'}
          </Badge>
        );
      },
    },
    {
      id: 'budget',
      accessorFn: (row) => row.budgetRevenue,
      header: 'Sales Budget',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.budgetRevenue)}
        </SensitiveData>
      ),
    },
    {
      id: 'actual-sales',
      accessorFn: (row) => row.actualRevenue,
      header: 'Actual Sales',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.actualRevenue)}
        </SensitiveData>
      ),
    },
    {
      id: 'sales-variance',
      accessorFn: (row) => row.salesVariancePercent,
      header: 'Sales Variance',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          <span className={getVarianceColor(-row.original.salesVariancePercent)}>
            {formatCurrency(row.original.salesVariance)} ({formatPercent(row.original.salesVariancePercent)} remaining)
          </span>
        </SensitiveData>
      ),
    },
    {
      id: 'cost-budget',
      accessorFn: (row) => row.budgetLabour + row.budgetExpense,
      header: 'Cost Budget',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(row.original.budgetLabour + row.original.budgetExpense)}
        </SensitiveData>
      ),
    },
    {
      id: 'actual-cost',
      accessorFn: (row) => row.actualLabour + row.actualExpense,
      header: 'Actual Costs',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          {formatCurrency(
            row.original.actualLabour + row.original.actualExpense,
          )}
        </SensitiveData>
      ),
    },
    {
      id: 'cost-variance',
      accessorFn: (row) => row.costVariancePercent,
      header: 'Cost Variance',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          <span className={getVarianceColor(-row.original.costVariancePercent)}>
            {formatCurrency(row.original.costVariance)} ({formatPercent(row.original.costVariancePercent)} remaining)
          </span>
        </SensitiveData>
      ),
    },

    {
      id: 'wip-amount',
      accessorFn: (row) => row.wipAmount,
      header: 'WIP Amount',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SensitiveData>
          <span className={getWIPColor(row.original.wipAmount)}>
            {formatCurrency(row.original.wipAmount)}
          </span>
        </SensitiveData>
      ),
    },
    {
      id: 'gross-profit',
      accessorFn: (row) => row.actualSales - row.actualCosts,
      header: 'Gross Profit',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const gp = row.original.actualSales - row.original.actualCosts;
        return (
          <SensitiveData>
            <span
              className={
                gp >= 0 ? 'text-success-600' : 'text-error-600'
              }
            >
              {formatCurrency(gp)}
            </span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'forecast-at-completion',
      accessorFn: (row) =>
        calculateForecastAtCompletion(
          row.actualLabour,
          row.actualExpense,
          row.budgetLabour,
          row.budgetExpense,
          row.completionPercent,
        ),
      header: 'Forecast at Completion',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const eac = calculateForecastAtCompletion(
          row.original.actualLabour,
          row.original.actualExpense,
          row.original.budgetLabour,
          row.original.budgetExpense,
          row.original.completionPercent,
        );
        return (
          <SensitiveData>{formatCurrency(eac)}</SensitiveData>
        );
      },
    },
    {
      id: 'allocated-hours',
      accessorFn: (row) => row.budgetHours,
      header: 'Resource Budget (hrs)',
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original.budgetHours == null) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <SensitiveData>
            {row.original.budgetHours.toLocaleString()} hrs
          </SensitiveData>
        );
      },
    },
    {
      id: 'actual-hours',
      accessorFn: (row) => row.actualHours,
      header: 'Actual Hours',
      meta: { align: 'right' },
      cell: ({ row }) => {
        if (row.original.actualHours == null) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <SensitiveData>
            {row.original.actualHours.toLocaleString()} hrs
          </SensitiveData>
        );
      },
    },
    {
      id: 'hours-variance',
      accessorFn: (row) => row.hoursVariancePercent,
      header: 'Hours Variance',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const v = row.original.hoursVariance;
        const vp = row.original.hoursVariancePercent;
        if (v == null || vp == null) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <SensitiveData>
            <span className={getVarianceColor(vp)}>
              {v > 0 ? '+' : ''}{v.toLocaleString()} hrs ({vp > 0 ? '+' : ''}{formatPercent(vp)})
            </span>
          </SensitiveData>
        );
      },
    },

    {
      id: 'completion-percentage',
      accessorFn: (row) => row.completionPercent,
      header: 'Completion Percentage',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Progress
            value={row.original.completionPercent}
            className="h-2 w-20"
          />
          <span className="text-xs text-muted-foreground w-10 text-right">
            {formatPercent(row.original.completionPercent, 0)}
          </span>
        </div>
      ),
    },
    {
      id: 'margin',
      accessorFn: (row) =>
        row.actualSales > 0
          ? ((row.actualSales - row.actualCosts) / row.actualSales) * 100
          : 0,
      header: 'Gross Margin',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const margin =
          row.original.actualSales > 0
            ? ((row.original.actualSales - row.original.actualCosts) /
                row.original.actualSales) *
              100
            : 0;
        return (
          <SensitiveData>
            <span className={margin >= 0 ? 'text-success-600' : 'text-error-600'}>
              {formatPercent(margin)}
            </span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'project-type',
      accessorFn: (row) => row.type,
      header: 'Project Type',
      cell: ({ row }) => {
        if (!row.original.type) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <Badge
            variant="outline"
            className={getProjectTypeColor(row.original.type)}
            disableHover
          >
            {formatProjectType(row.original.type)}
          </Badge>
        );
      },
    },
    {
      id: 'phase',
      header: 'Phase/Stage',
      cell: () => <span className="text-muted-foreground text-sm">-</span>,
    },
    {
      id: 'start-date',
      accessorFn: (row) => row.startDate,
      header: 'Start Date',
      cell: ({ row }) => formatDate(row.original.startDate),
    },
    {
      id: 'end-date',
      accessorFn: (row) => row.endDate,
      header: 'End Date',
      cell: ({ row }) => formatDate(row.original.endDate),
    },
    {
      id: 'days-remaining',
      accessorFn: (row) => calculateDaysRemaining(row.endDate),
      header: 'Days Remaining',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const days = calculateDaysRemaining(row.original.endDate);
        if (days === null) {
          return (
            <span className="text-muted-foreground text-sm">-</span>
          );
        }
        return (
          <span className={getDaysRemainingColor(days)}>
            {days > 0
              ? `${days} days`
              : days === 0
                ? 'Today'
                : 'Overdue'}
          </span>
        );
      },
    },
    {
      id: 'next-milestone',
      accessorFn: (row) => {
        const m = getNextMilestone(row.id, ref.milestones);
        return m?.name ?? '';
      },
      header: 'Next Milestone',
      cell: ({ row }) => {
        const milestone = getNextMilestone(
          row.original.id,
          ref.milestones,
        );
        if (!milestone) {
          return (
            <span className="text-muted-foreground text-sm">-</span>
          );
        }
        return (
          <div>
            <div className="font-medium text-sm">{milestone.name}</div>
            <div className="text-xs text-muted-foreground">
              {formatDate(milestone.dueDate)}
            </div>
          </div>
        );
      },
    },
    {
      id: 'team-members',
      accessorFn: (row) =>
        getAllocatedResources(row.id, ref.resourceAllocations),
      header: 'Team Members',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const count = getAllocatedResources(
          row.original.id,
          ref.resourceAllocations,
        );
        return count > 0
          ? `${count} member${count !== 1 ? 's' : ''}`
          : '-';
      },
    },

    {
      id: 'tasks-overdue',
      accessorFn: (row) => getOverdueTasks(row.id, ref.tasks),
      header: 'Overdue Tasks',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const count = getOverdueTasks(row.original.id, ref.tasks);
        return count > 0 ? (
          <Badge variant="destructive" disableHover>
            {count}
          </Badge>
        ) : (
          <span className="text-success-600 text-sm">0</span>
        );
      },
    },
    {
      id: 'overdue-milestones',
      accessorFn: (row) => getOverdueMilestones(row.id, ref.milestones),
      header: 'Overdue Milestones',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const count = getOverdueMilestones(
          row.original.id,
          ref.milestones,
        );
        return count > 0 ? (
          <Badge variant="destructive" disableHover>
            {count}
          </Badge>
        ) : (
          <span className="text-success-600 text-sm">0</span>
        );
      },
    },
    {
      id: 'last-updated',
      accessorFn: (row) => row.updatedAt,
      header: 'Last Updated',
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <span className="text-sm">
                {formatRelativeTime(row.original.updatedAt)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {formatDate(row.original.updatedAt)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      id: 'milestones-completed',
      accessorFn: (row) => getCompletedMilestones(row.id, ref.milestones),
      header: 'Milestones Completed',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const completed = getCompletedMilestones(
          row.original.id,
          ref.milestones,
        );
        const total = getTotalMilestones(
          row.original.id,
          ref.milestones,
        );
        return total > 0 ? (
          <span>
            {completed} / {total}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        );
      },
    },
    {
      id: 'gross-margin',
      accessorFn: (row) =>
        row.actualSales > 0
          ? ((row.actualSales - row.actualCosts) / row.actualSales) * 100
          : 0,
      header: 'Gross Margin',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const margin =
          row.original.actualSales > 0
            ? ((row.original.actualSales - row.original.actualCosts) /
                row.original.actualSales) *
              100
            : 0;
        return (
          <SensitiveData>
            <span className={margin >= 0 ? 'text-success-600' : 'text-error-600'}>
              {formatPercent(margin)}
            </span>
          </SensitiveData>
        );
      },
    },
    {
      id: 'billing-status',
      header: 'Billing Status',
      cell: () => <span className="text-muted-foreground text-sm">-</span>,
    },
    {
      id: 'utilisation-rate',
      accessorFn: (row) => getUtilizationRate(row),
      header: 'Utilisation Rate',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const rate = getUtilizationRate(row.original);
        return (
          <span
            className={
              rate > 100
                ? 'text-red-600'
                : rate > 80
                  ? 'text-amber-600'
                  : 'text-green-600'
            }
          >
            {formatPercent(rate)}
          </span>
        );
      },
    },
    {
      id: 'duration',
      accessorFn: (row) =>
        calculateDuration(row.startDate, row.endDate),
      header: 'Duration',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const d = calculateDuration(
          row.original.startDate,
          row.original.endDate,
        );
        return `${d} day${d !== 1 ? 's' : ''}`;
      },
    },
    {
      id: 'risk-level',
      header: 'Risk Level',
      cell: () => <span className="text-muted-foreground text-sm">-</span>,
    },
    {
      id: 'quality-score',
      header: 'Quality Score',
      cell: () => <span className="text-muted-foreground text-sm">-</span>,
    },
  ];
}

// ── Component ────────────────────────────────────────────────────────────

export function ProjectsGrid({
  data,
  referenceData,
  columnDefinitions,
  columnSorts,
  onColumnSortChange,
  onRowClick,
  loading = false,
  isTransitioning = false,
  pageSize = 25,
  hasActiveFilters = false,
  pagination,
  onPageChange,
  onPageSizeChange,
}: ProjectsGridProps) {
  // Get dynamic status labels from context
  const { statusLabels } = useProjectStatuses();

  // Build full column set with cell renderers (memoised on reference data)
  const allColumns = useMemo(
    () => buildProjectColumns(referenceData, statusLabels),
    [referenceData, statusLabels],
  );

  // Filter and order columns to match the visible ColumnDefinitions
  const visibleColumnDefs = useMemo(() => {
    const visibleDefs = columnDefinitions
      .filter((cd) => cd.visible)
      .sort((a, b) => a.order - b.order);

    const columnMap = new Map(allColumns.map((c) => [c.id, c]));
    return visibleDefs
      .map((cd) => columnMap.get(cd.id))
      .filter((c): c is ColumnDef<Project, unknown> => c !== undefined);
  }, [columnDefinitions, allColumns]);

  // Build TanStack visibility state from ColumnDefinitions
  const columnVisibility = useMemo(
    () => columnDefinitionsToVisibility(columnDefinitions),
    [columnDefinitions],
  );

  // Empty state messages
  const emptyMessage = hasActiveFilters ? (
    <div className="space-y-2">
      <p className="text-lg font-medium">No projects found</p>
      <p className="text-sm">Try adjusting your filters or search term</p>
    </div>
  ) : (
    <div className="space-y-2">
      <p className="text-lg font-medium">No projects yet</p>
      <p className="text-sm">Get started by creating your first project</p>
    </div>
  );

  // Pagination content
  const paginationContent =
    !loading && pagination && pagination.totalItems > 0 && onPageChange ? (
      <PaginationControls
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showPageSizeSelector={true}
        className="pt-4 border-t"
      />
    ) : null;

  return (
    <DataTableEnhanced<Project, unknown>
      columns={visibleColumnDefs}
      data={data}
      columnVisibility={columnVisibility}
      columnSorts={columnSorts}
      onColumnSortChange={onColumnSortChange}
      loading={loading}
      loadingRowCount={pageSize}
      isTransitioning={isTransitioning}
      emptyMessage={emptyMessage}
      onRowClick={onRowClick}
      getRowId={(row) => String(row.id)}
      dataTour="project-list-table"
      paginationContent={paginationContent}
    />
  );
}
